/**
 * `FashionParams` — listy wartosci i schemat Zoda (docs/VERTICALS.md
 * § FashionParams).
 *
 * DLACZEGO OSOBNY PLIK: `src/shared/scene/spec.ts` musi zaimportowac ten schemat
 * (buduje z niego `VerticalParamsSchema`), a `fashion.ts` importuje
 * `registry.ts`, ktore importuje WARTOSC `VERTICALS` ze `spec.ts`. Gdyby schemat
 * mieszkal w `fashion.ts`, powstalby cykl `spec -> fashion -> registry -> spec`,
 * ktory w ESM wywala sie na TDZ (`VERTICAL_IDS = VERTICALS` przy niezainicjowanym
 * `VERTICALS`). Ten plik jest LISCIEM: importuje wylacznie `zod`.
 *
 * `fashion.ts` re-eksportuje calosc, wiec konsumenci importuja z `./fashion`
 * i nie musza wiedziec o tym podziale.
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Listy wartosci (kolejnosc DOKLADNIE jak w docs/VERTICALS.md)
// ─────────────────────────────────────────────────────────────────────────────

export const FASHION_HERO_ANGLES = [
  'three_quarter_front',
  'profile_lateral',
  'profile_medial',
  'top_down_flat',
  '45_elevated',
  'heel_back',
  'interior_open',
  'sole_up',
  'detail_hardware',
] as const;
export type FashionHeroAngle = (typeof FASHION_HERO_ANGLES)[number];

/** Katy, na ktorych podeszwa JEST widoczna — napedza konflikt `soleVisible`. */
export const FASHION_SOLE_VISIBLE_ANGLES: readonly FashionHeroAngle[] = [
  'sole_up',
  'profile_lateral',
  'profile_medial',
];

/** Katy, na ktorych wnetrze JEST widoczne — napedza konflikt `interiorVisible`. */
export const FASHION_INTERIOR_VISIBLE_ANGLES: readonly FashionHeroAngle[] = ['interior_open', 'top_down_flat'];

export const FASHION_ARRANGEMENTS = [
  'single',
  'mirrored_pair',
  'stacked_pair',
  'one_upright_one_flat',
  'fanned_row',
  'nested_trio',
  'exploded_components',
  'family_lineup',
] as const;
export type FashionArrangement = (typeof FASHION_ARRANGEMENTS)[number];

export const FASHION_SUPPORTS = [
  'invisible_stand',
  'acrylic_riser',
  'levitation_float',
  'leaning_block',
  'tissue_stuffed',
  'ghost_mannequin',
  'hanging_strap',
  'magnetic_hover',
] as const;
export type FashionSupport = (typeof FASHION_SUPPORTS)[number];

/** Podpory zgodne z lewitacja — pozostale koliduja z `levitation.enabled`. */
export const FASHION_FLOATING_SUPPORTS: readonly FashionSupport[] = ['levitation_float', 'magnetic_hover'];

export const FASHION_SHADOW_ANCHORS = ['none', 'soft_contact', 'cast_only'] as const;
export type FashionShadowAnchor = (typeof FASHION_SHADOW_ANCHORS)[number];

export const FASHION_STRAP_STATES = ['down', 'over_shoulder', 'held_up', 'coiled', 'crossbody', 'knotted'] as const;
export type FashionStrapState = (typeof FASHION_STRAP_STATES)[number];

export const FASHION_LACE_STYLINGS = ['tied_neat', 'loose_draped', 'tucked_in', 'unlaced', 'double_bow'] as const;
export type FashionLaceStyling = (typeof FASHION_LACE_STYLINGS)[number];

export const FASHION_CLOSURE_STATES = ['closed', 'half_open', 'fully_open', 'flap_lifted'] as const;
export type FashionClosureState = (typeof FASHION_CLOSURE_STATES)[number];

export const FASHION_TEXTURES = [
  'leather_grain',
  'full_grain_pull_up',
  'suede_nap',
  'nubuck',
  'patent_specular',
  'canvas_weave',
  'knit_mesh',
  'croc_emboss',
  'saffiano_cross',
  'woven_intrecciato',
  'ripstop',
  'shearling',
] as const;
export type FashionTexture = (typeof FASHION_TEXTURES)[number];

export const FASHION_SHEENS = ['matte', 'satin', 'high_gloss'] as const;
export type FashionSheen = (typeof FASHION_SHEENS)[number];

export const FASHION_HARDWARE_ACCENTS = ['gold', 'brass', 'silver', 'gunmetal', 'matte_black', 'none'] as const;
export type FashionHardwareAccent = (typeof FASHION_HARDWARE_ACCENTS)[number];

export const FASHION_LOGO_POLICIES = ['must_be_sharp', 'subtle_visible', 'hidden_from_view'] as const;
export type FashionLogoPolicy = (typeof FASHION_LOGO_POLICIES)[number];

export const FASHION_WEAR_STATES = ['pristine_new', 'lightly_worn', 'vintage_patina'] as const;
export type FashionWearState = (typeof FASHION_WEAR_STATES)[number];

export const FASHION_WORN_STATES = [
  'unworn',
  'on_foot',
  'on_foot_mid_stride',
  'in_hand',
  'over_forearm',
  'on_shoulder',
  'crossbody_on_torso',
  'seated_step',
] as const;
export type FashionWornState = (typeof FASHION_WORN_STATES)[number];

export const FASHION_BODY_CROPS = [
  'feet_ankle',
  'calf_down',
  'knee_down',
  'hands_only',
  'torso_hip',
  'full_figure',
] as const;
export type FashionBodyCrop = (typeof FASHION_BODY_CROPS)[number];

export const FASHION_SOCK_STYLINGS = ['bare_ankle', 'no_show', 'crew_white', 'ribbed_tonal', 'sheer'] as const;
export type FashionSockStyling = (typeof FASHION_SOCK_STYLINGS)[number];

export const FASHION_TROUSER_BREAKS = ['cropped', 'cuffed_denim', 'wide_leg_covering', 'none'] as const;
export type FashionTrouserBreak = (typeof FASHION_TROUSER_BREAKS)[number];

export const FASHION_NAIL_STYLES = ['bare', 'nude', 'red', 'french'] as const;
export type FashionNailStyle = (typeof FASHION_NAIL_STYLES)[number];

/**
 * `talentIdentitySource` ma dokladnie jedna wartosc i to jest decyzja swiadoma
 * (docs/VERTICALS.md): NIGDY nie opisujemy tekstem etnicznosci ani wieku osoby.
 */
export const FASHION_TALENT_IDENTITY_SOURCES = ['reference_image_only'] as const;
export type FashionTalentIdentitySource = (typeof FASHION_TALENT_IDENTITY_SOURCES)[number];

export const FASHION_SURFACE_PRESETS = [
  'seamless_white',
  'seamless_tonal',
  'travertine',
  'brushed_steel',
  'terrazzo',
  'raw_concrete',
  'linen',
  'oak_plank',
  'glass_riser',
  'sand',
  'water_shallow',
  'studio_paper_sweep',
] as const;
export type FashionSurfacePreset = (typeof FASHION_SURFACE_PRESETS)[number];

export const FASHION_PROP_KIT = [
  'dust_bag',
  'shoe_box',
  'tissue_paper',
  'denim_cuff',
  'jewelry',
  'sunglasses',
  'perfume_bottle',
  'dried_botanical',
  'stone_slab',
  'fabric_swatch',
  'shoe_tree',
] as const;
export type FashionPropKitItem = (typeof FASHION_PROP_KIT)[number];

export const FASHION_MOTION_CUES = [
  'static',
  'fabric_wind_light',
  'fabric_wind_strong',
  'dust_kick',
  'ribbon_swirl',
] as const;
export type FashionMotionCue = (typeof FASHION_MOTION_CUES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Schemat
// ─────────────────────────────────────────────────────────────────────────────

export const FashionLevitationSchema = z.strictObject({
  enabled: z.boolean(),
  heightPct: z.number().min(0).max(100).optional(),
  tiltDeg: z.int().min(-45).max(45).optional(),
  shadowAnchor: z.enum(FASHION_SHADOW_ANCHORS).optional(),
  motionBlur: z.boolean().optional(),
});
export type FashionLevitation = z.infer<typeof FashionLevitationSchema>;

export const FashionHandModelDetailsSchema = z.strictObject({
  nails: z.enum(FASHION_NAIL_STYLES).optional(),
  jewelry: z.boolean().optional(),
  watch: z.boolean().optional(),
});
export type FashionHandModelDetails = z.infer<typeof FashionHandModelDetailsSchema>;

/**
 * `FashionParams`. Wszystkie pola poza dyskryminatorem sa OPCJONALNE: pusty
 * `{ vertical: 'fashion' }` musi byc walidnym `verticalParams` (tak dziala
 * `emptyScene` w `src/shared/scene/defaults.ts`), a brak parametru znaczy
 * "nie sterujemy tym" — z wyjatkiem parametrow TWARDYCH, gdzie deskryptor sam
 * definiuje domysl i emituje klauzule takze przy `undefined`. Guardrail nie moze
 * znikac przez pominiecie pola w JSON-ie.
 *
 * `strictObject` — nieznany klucz to blad walidacji, wiec patch LLM nie moze
 * wstrzyknac wlasnego pola do `verticalParams`.
 */
export const FashionParamsSchema = z.strictObject({
  vertical: z.literal('fashion'),

  // Prezentacja i geometria
  heroAngle: z.enum(FASHION_HERO_ANGLES).optional(),
  arrangement: z.enum(FASHION_ARRANGEMENTS).optional(),
  support: z.enum(FASHION_SUPPORTS).optional(),
  levitation: FashionLevitationSchema.optional(),
  shapeIntegrity: z.boolean().optional(),
  soleVisible: z.boolean().optional(),
  interiorVisible: z.boolean().optional(),
  strapState: z.enum(FASHION_STRAP_STATES).optional(),
  laceStyling: z.enum(FASHION_LACE_STYLINGS).optional(),
  closureState: z.enum(FASHION_CLOSURE_STATES).optional(),

  // Material i wykonczenie
  textureEmphasis: z.array(z.enum(FASHION_TEXTURES)).max(FASHION_TEXTURES.length).optional(),
  sheen: z.enum(FASHION_SHEENS).optional(),
  specularControlPct: z.number().min(0).max(100).optional(),
  hardwareAccent: z.enum(FASHION_HARDWARE_ACCENTS).optional(),
  logoPolicy: z.enum(FASHION_LOGO_POLICIES).optional(),
  stitchColorLock: z.boolean().optional(),
  colorwayLock: z.boolean().optional(),
  wearState: z.enum(FASHION_WEAR_STATES).optional(),

  // Na ciele
  wornState: z.enum(FASHION_WORN_STATES).optional(),
  bodyCrop: z.enum(FASHION_BODY_CROPS).optional(),
  sockStyling: z.enum(FASHION_SOCK_STYLINGS).optional(),
  trouserBreak: z.enum(FASHION_TROUSER_BREAKS).optional(),
  handModelDetails: FashionHandModelDetailsSchema.optional(),
  talentIdentitySource: z.enum(FASHION_TALENT_IDENTITY_SOURCES).optional(),

  // Set i styling
  surfacePreset: z.enum(FASHION_SURFACE_PRESETS).optional(),
  propKit: z.array(z.enum(FASHION_PROP_KIT)).max(FASHION_PROP_KIT.length).optional(),
  motionCue: z.enum(FASHION_MOTION_CUES).optional(),
  editorialLevel: z.number().min(0).max(1).optional(),
});
export type FashionParams = z.infer<typeof FashionParamsSchema>;

/**
 * Jawne domysly pakietu. `emptyScene` zapisuje tylko dyskryminator, ale composer
 * i seed moga wstawic ten obiekt, zeby panel byl od razu wypelniony, a asset
 * przechodzil checkliste approvera.
 */
export function defaultFashionParams(): FashionParams {
  return {
    vertical: 'fashion',
    shapeIntegrity: true,
    colorwayLock: true,
    stitchColorLock: true,
    hardwareAccent: 'none',
    logoPolicy: 'must_be_sharp',
    talentIdentitySource: 'reference_image_only',
    arrangement: 'single',
    wearState: 'pristine_new',
    wornState: 'unworn',
    editorialLevel: 0.25,
  };
}
